/*
 * Copyright (c) 2026 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from "react";


// CSS styles for modal
const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    width: '750px',
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'relative',
    textShadow: 'none',
    color: 'black',
    fontFamily: 'arial',
    textAlign: 'left',
    fontSize: 'small'
};


interface TermsAndConditionsProps {
    onClose: () => void
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({onClose}) => {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
            <h2>Terms and Conditions</h2>
            Effective Date: June 5th, 2026<br/>
            Welcome to Monster Forge by Green God Games, LLC.
            By accessing or using our website and services, you agree to comply with and be bound by these Terms and Conditions.
            Please read them carefully.
            <ol>
                <li>
                    <b>Acceptance of Terms</b><br/>
                    By creating an account and using Monster Forge, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy.
                    If you do not agree to these terms, you may not use our services.
                </li>
                <li>
                    <b>Age Requirement</b><br/>
                    You must be at least 13 years old to create an account and use Monster Forge.
                    By creating an account, you represent and warrant that you meet this age requirement.
                    We do not knowingly collect information from or allow children under 13 to use our services.
                    If you are under 13, you are not permitted to use this service.
                </li>
                <li>
                    <b>Account Responsibilities</b><br/>
                    You are responsible for:
                    <ul>
                        <li>Maintaining the confidentiality of your account credentials.</li>
                        <li>All activities that occur under your account.</li>
                        <li>Notifying us immediately of any unauthorized use of your account.</li>
                    </ul>
                    Green God Games, LLC is not liable for any loss or damage arising from your failure to protect your account information.
                </li>
                <li>
                    <b>Data Value and Backup</b><br/>
                    While we strive to provide reliable service and data storage, you acknowledge and agree that:
                    <ul>
                        <li>All content you create and store (including monsters, characters, and other data) has <b>no monetary value</b>.</li>
                        <li>We are not liable for any loss, corruption, or deletion of your data for any reason, including but not limited to technical failures, service interruptions, or account termination.</li>
                        <li>You are solely responsible for maintaining your own backups of any content you create.</li>
                        <li>We provide data storage as a convenience and make no guarantees regarding data retention or availability.</li>
                    </ul>
                </li>
                <li>
                    <b>Prohibited Content and Conduct</b><br/>
                    You agree not to create, upload, share, or make public any content that:
                    <ul>
                        <li>Is pornographic, sexually explicit, or adult-oriented in nature.</li>
                        <li><b>Sexually depicts or sexualizes minors, or any person or character who appears to be a minor.</b> Such content is strictly prohibited, will be removed immediately, will result in termination of your account, and will be reported to law enforcement and the National Center for Missing &amp; Exploited Children (NCMEC) as required by law.</li>
                        <li>Constitutes hate speech, or attacks, demeans, dehumanizes, or promotes hatred or violence against people based on race, ethnicity, national origin, religion, gender, gender identity, sexual orientation, disability, age, or other protected characteristic; or displays real-world hate symbols.</li>
                        <li>Harasses, bullies, threatens, or targets a real person; discloses another person's private or personal information ("doxxing"); or impersonates any person or entity.</li>
                        <li>Promotes, glorifies, or provides support for terrorism, violent extremism, or real-world atrocities.</li>
                        <li>Promotes or encourages suicide, self-harm, or eating disorders.</li>
                        <li>Is real-world political campaigning or partisan messaging, or religious proselytizing. (Fantasy factions, deities, and religions depicted within game content are permitted.)</li>
                        <li>Infringes on the intellectual property rights of others, including copyrighted text, artwork, or trademarked material (such as officially published game content or art) that you do not have the right to use.</li>
                        <li>Contains malware, viruses, spam, scams, or any harmful or deceptive code or links.</li>
                        <li>Violates any applicable local, state, national, or international law.</li>
                    </ul>
                    Monster Forge is a fantasy tabletop role-playing tool. Depictions of fantasy or in-game violence, combat, monsters, and dark or horror themes are permitted. This exception does not allow content whose purpose is to celebrate, promote, or trivialize real-world violence, cruelty, or harm toward real people or groups.<br/>
                    If you encounter content that you believe violates these terms, please report it to <a href="mailto:support@GreenGodGames.com" target="_blank" rel="noreferrer">support@GreenGodGames.com</a>. We reserve the right to remove any content that violates these terms and to suspend or terminate accounts of users who violate these policies.
                </li>
                <li>
                    <b>Content Sharing and Public Content</b><br/>
                    When you share a monster (by making it accessible through a shareable link, or by submitting it to be made public):
                    <ul>
                        <li>You are responsible for ensuring that any content you share with other users or make public complies with these Terms and Conditions.</li>
                        <li>By making content public or sharing it with others, you grant Green God Games, LLC a non-exclusive, worldwide, royalty-free license to display, distribute, and use that content solely for the purpose of operating and improving the service.</li>
                        <li>You retain ownership of all content you create, but you are responsible for ensuring you have the right to share it.</li>
                        <li>Content submitted to be made public is subject to review and approval at our sole discretion; we may decline to approve, or may later remove, any content for any reason.</li>
                        <li>Content shared through a direct link may be accessible to anyone who has the link, including before any review, and remains your responsibility.</li>
                        <li>We reserve the right to remove any shared or publicly available content that violates these terms.</li>
                    </ul>
                </li>
                <li>
                    <b>Service Modifications and Availability</b><br/>
                    Green God Games, LLC reserves the right to:
                    <ul>
                        <li>Modify, suspend, or discontinue any aspect of the service at any time without notice.</li>
                        <li>Change subscription pricing, features, or terms with reasonable notice to active subscribers.</li>
                        <li>Perform maintenance that may temporarily interrupt service availability.</li>
                    </ul>
                    We are not liable for any modification, suspension, or discontinuation of the service.
                </li>
                <li>
                    <b>Subscription and Billing</b><br/>
                    If you subscribe to a paid plan:
                    <ul>
                        <li>You agree to pay all fees associated with your chosen subscription tier.</li>
                        <li>Subscriptions automatically renew unless canceled before the renewal date.</li>
                        <li>We do not provide refunds for partial subscription periods.</li>
                        <li>You can cancel your subscription at any time, and it will remain active until the end of the current billing period.</li>
                    </ul>
                </li>
                <li>
                    <b>Intellectual Property</b><br/>
                    <ul>
                        <li>Monster Forge and all associated materials, including but not limited to software, design, graphics, and documentation, are the property of Green God Games, LLC and are protected by copyright and other intellectual property laws.</li>
                        <li>You may not copy, modify, distribute, sell, or reverse engineer any part of our service.</li>
                        <li>Content you create using Monster Forge remains your property, but you must ensure you have the rights to any third-party content you incorporate (such as images).</li>
                        <li>
                            <b>Wizards of the Coast (WotC) and other third-party game content.</b> Some monsters
                            published by Wizards of the Coast have been released under the Open Game License (OGL),
                            and those are made available within Monster Forge's monster list for your use.
                            However, <b>recreating and sharing monsters or other content from WotC books (or any other
                            publisher) that have <u>not</u> been released under the OGL is a violation of the publisher's
                            copyright and is strictly prohibited.</b> You are solely responsible for ensuring that any
                            monster you share, submit for approval, or make public does not reproduce copyrighted
                            material that has not been opened under the OGL or that you do not otherwise have the
                            right to use.
                        </li>
                    </ul>
                </li>
                <li>
                    <b>Copyright Policy (DMCA)</b><br/>
                    We respect the intellectual property rights of others and expect our users to do the same. If you believe content on Monster Forge infringes your copyright, please send a notice to our designated agent at <a href="mailto:support@GreenGodGames.com" target="_blank" rel="noreferrer">support@GreenGodGames.com</a> that includes:
                    <ul>
                        <li>Your contact information (name, address, email).</li>
                        <li>A description of the copyrighted work you claim has been infringed.</li>
                        <li>The location (e.g., the link) of the allegedly infringing content.</li>
                        <li>A statement that you have a good-faith belief the use is not authorized by the copyright owner, its agent, or the law.</li>
                        <li>A statement, made under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or are authorized to act on the owner's behalf.</li>
                        <li>Your physical or electronic signature.</li>
                    </ul>
                    We will respond to valid notices, may remove or disable access to the allegedly infringing content, and may terminate the accounts of repeat infringers.
                </li>
                <li>
                    <b>Limitation of Liability</b><br/>
                    To the fullest extent permitted by law:
                    <ul>
                        <li>Monster Forge is provided "as is" without warranties of any kind, either express or implied.</li>
                        <li>Green God Games, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service.</li>
                        <li>Our total liability for any claims related to the service shall not exceed the amount you paid for the service in the 12 months preceding the claim.</li>
                    </ul>
                </li>
                <li>
                    <b>Indemnification</b><br/>
                    You agree to indemnify, defend, and hold harmless Green God Games, LLC and its officers, members, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with: (a) content you create, upload, share, or make public; (b) your use of the service; or (c) your violation of these Terms and Conditions or the rights of any third party.
                </li>
                <li>
                    <b>Account Termination</b><br/>
                    We reserve the right to suspend or terminate your account at any time, with or without cause, including but not limited to:
                    <ul>
                        <li>Violation of these Terms and Conditions.</li>
                        <li>Fraudulent, abusive, or illegal activity.</li>
                        <li>Extended periods of inactivity (with reasonable notice).</li>
                    </ul>
                    Upon termination, you will lose access to your account and all associated data.
                </li>
                <li>
                    <b>Governing Law</b><br/>
                    These Terms and Conditions shall be governed by and construed in accordance with the laws of the United States and the state in which Green God Games, LLC is registered, without regard to conflict of law principles.
                </li>
                <li>
                    <b>Changes to These Terms</b><br/>
                    We may update these Terms and Conditions from time to time to reflect changes to our services or for legal or regulatory reasons.
                    We will notify users of significant changes through email or prominent notice on our website.
                    Continued use of the service after changes are posted constitutes acceptance of the updated terms.
                </li>
                <li>
                    <b>Contact Us</b><br/>
                    If you have any questions about these Terms and Conditions, please contact us at:<br/>
                    Email: <a href="mailto:support@GreenGodGames.com" target="_blank" rel="noreferrer">support@GreenGodGames.com</a>
                </li>
            </ol>
            <br/>
            <div style={{textAlign: 'right'}}>
                <button onClick={() => onClose()}>Close</button>
            </div>
        </div>
     </div>
    );
  };

  export default TermsAndConditions;
